import React, {useEffect, useMemo, useState} from "react";
import { profilesApi, api as axiosApi } from "../../api";
import "../../css/pages/ProfilesPage.css";
import UserProfileCard from "./UserProfileCard";
import HrnbContainer, { HrnbCol, HrnbRow } from "../controls/HrnbContainer";
import HrnbSkeleton from "../controls/HrnbSkeleton";
import HrnbAlert from "../controls/HrnbAlert";
import { SkeletonCard } from "../controls/HrnbSkeleton";
import HrnbSearchFilter from "../controls/HrnbSearchFilter";

function escapeRegExp(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\__CODE_BLOCK_0__amp;");
}
function highlightMatch(text = "", q = "") {
    if (!q) return text;
    const re = new RegExp(`(${escapeRegExp(q)})`, "ig");
    const parts = text.split(re);
    return parts.map((part, i) =>
        re.test(part) ? <mark key={i}>{part}</mark> : <span key={i}>{part}</span>
    );
}


export default function ProfilesPage() {
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");          // значение поля ввода
    const [searchQuery, setSearchQuery] = useState(""); // значение для фильтрации (debounced from HrnbSearchFilter)
    const [filterType, setFilterType] = useState("");

    // собираем типы профилей
    const profileTypes = useMemo(() => {
        const setTypes = new Set();
        profiles.forEach((p) => {
            const t = (p?.profileType ?? "").toString().trim();
            if (t) setTypes.add(t);
        });
        return Array.from(setTypes).sort();
    }, [profiles]);

    const typeOptions = profileTypes.map((t) => ({ value: t, label: t }));

    const currentProfileId =
        JSON.parse(localStorage.getItem("user") || "{}")?.profiles?.[0]?.id || null;

    useEffect(() => {
        let mounted = true;
        const fetchProfiles = async () => {
            setLoading(true);
            setError("");
            try {
                let data;
                if (profilesApi && typeof profilesApi.list === "function") {
                    data = await profilesApi.list({ size: 1000 });
                    // API v2 возвращает { content: [...], page: {...} }
                    const profilesList = Array.isArray(data?.content)
                        ? data.content
                        : Array.isArray(data) ? data : [];
                    if (mounted) setProfiles(profilesList);
                } else {
                    const resp = await axiosApi.get("/profiles");
                    const profilesList = Array.isArray(resp.data?.content)
                        ? resp.data.content
                        : Array.isArray(resp.data) ? resp.data : [];
                    if (mounted) setProfiles(profilesList);
                }
            } catch (err) {
                console.error("Ошибка загрузки профилей:", err);
                if (mounted) setError("Ошибка загрузки профилей");
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetchProfiles();
        return () => {
            mounted = false;
        };
    }, []);

    // фильтрация по searchQuery (debounced) и type
    const filteredProfiles = useMemo(() => {
        const q = (searchQuery || "").trim().toLowerCase();
        return profiles
            .filter((p) => p.id !== currentProfileId)
            .filter((p) => (filterType ? p.profileType === filterType : true))
            .filter((p) => {
                if (!q) return true;
                return (p.displayName || "").toLowerCase().includes(q);
            })
            .map((p) => ({
                ...p,
                highlightedName: highlightMatch(p.displayName || "", searchQuery),
            }));
    }, [profiles, currentProfileId, filterType, searchQuery]);

    if (loading) {
        return (
            <HrnbContainer>
                <HrnbRow style={{ marginBottom: 16 }}>
                    <HrnbSkeleton width="240px" height="32px" />
                </HrnbRow>
                <HrnbRow style={{ marginBottom: 16 }}>
                    <HrnbCol col={12}>
                        <HrnbSkeleton height="48px" />
                    </HrnbCol>
                </HrnbRow>
                <HrnbRow className="hrnb-row--center hrnb-row--align-start">
                    {Array.from({ length: 8 }).map((_, i) => (
                        <HrnbCol col={4} xl={3} key={i}>
                            <SkeletonCard />
                        </HrnbCol>
                    ))}
                </HrnbRow>
            </HrnbContainer>
        );
    }
    if (error) {
        return (
            <HrnbContainer>
                <HrnbRow className="hrnb-row--center hrnb-row--align-center">
                    <HrnbAlert type="danger">{error}</HrnbAlert>
                </HrnbRow>
            </HrnbContainer>
        );
    }

    if (!profiles.length) {
        return (
            <HrnbContainer>
                <HrnbRow className="hrnb-row--center hrnb-row--align-center">
                    <div>Публичные профили не найдены</div>
                </HrnbRow>
            </HrnbContainer>
        );
    }

    return (
        <HrnbContainer>
            <HrnbRow className="hrnb-row--center hrnb-row--align-center">
                <h2>Герои рифмы и бита</h2>
                <div style={{ marginLeft: 12, color: "#666" }}>
                    Показано <strong>{filteredProfiles.length}</strong> из <strong>{profiles.length}</strong>
                </div>
            </HrnbRow>

            <HrnbRow className="hrnb-row--center hrnb-row--align-center" style={{ marginBottom: 16 }}>
                <HrnbCol col={12}>
                    <HrnbSearchFilter
                        search={search}
                        onSearchChange={setSearch}
                        onSearchSubmit={setSearchQuery} // debounce теперь внутри компонента
                        filterValue={filterType}
                        onFilterChange={setFilterType}
                        filterOptions={typeOptions}
                        onClear={() => {
                            setSearch("");
                            setSearchQuery("");
                            setFilterType("");
                        }}
                        debounceMs={300}
                    />
                </HrnbCol>
            </HrnbRow>

            <HrnbRow className="hrnb-row--center hrnb-row--align-start">
                {filteredProfiles.map((profile) => (
                    <HrnbCol col={4} xl={3} key={profile.id}>
                        <UserProfileCard profile={profile} />
                    </HrnbCol>
                ))}
            </HrnbRow>
        </HrnbContainer>
    );
}